import React, {useState} from 'react';

import {TypeLocalNameEnum} from 'domain/usecases';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText, getAddressforMaps} from 'presentation/utils';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {openGoogleMaps} from 'presentation/modules/booking/shared/utils/openGoogleMaps';

import {UseUnitCardProps} from './types';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {normalizeText} from 'presentation/utils/normalizeText';

const {fromMe} = translations['pt-br'].commons;
const {cityReference, neighborhoodReference, telehealthAddress} =
	translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function useUnitCard({
	isTelemedicine,
	isNotSelf,
	showDistanceReference,
	nmBairroOrigem,
	phoneNumbers,
	address,
	unitName,
}: UseUnitCardProps) {
	const {isMobile} = useMediaQueries();
	const bookingData = useBookingStore(state => state.bookingData);
	const [openPhoneDrawerState, setOpenPhoneDrawerState] = useState<boolean>(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [showEmptyPhoneSnackbar, setShowEmptyPhoneSnackbar] = useState(false);
	const [showDetailsDrawerState, setShowDetailsDrawerState] = useState(false);
	const {typeLocalName, cityName, bookingType} = bookingData;

	const isExam = bookingType === BookingTypeEnum.EXAM;

	const dataTagId = (() => {
		if (isTelemedicine) {
			return AppointmentBookingEnum.SELECIONOU_TELECONSULTA;
		}
		if (isNotSelf) {
			return AppointmentBookingEnum.SELECIONOU_REDE_CREDENCIADA;
		}
		if (showDistanceReference) {
			return AppointmentBookingEnum.SELECIONOU_UNIDADE_POR_RAIO;
		}
		if (isExam) {
			return AppointmentBookingEnum.SELECIONOU_UNIDADE_EXAME;
		}
		return AppointmentBookingEnum.SELECIONOU_UNIDADE_POR_LOCAL;
	})();

	const getDistanceFilterDescription = () => {
		if (typeLocalName === TypeLocalNameEnum.CIDADE) {
			return `${cityReference} ${formatText(cityName)}`;
		}
		if (typeLocalName === TypeLocalNameEnum.COORDENADAS) {
			return fromMe;
		}

		return `${neighborhoodReference} ${formatText(nmBairroOrigem)}`;
	};

	const distanceFilterDescription = getDistanceFilterDescription();
	const formattedAddress = getAddressforMaps(address);
	const handleOpenGoogleMaps = () => openGoogleMaps(formattedAddress?.addressForMaps ?? '');

	const phoneNumbersLength = phoneNumbers?.length ?? 0;
	const isPhoneNumberEmpty = phoneNumbersLength === 0;
	const hasMoreThanOnePhoneNumber = phoneNumbersLength > 1;
	const shouldCallDirectlyToOnlyPhone = phoneNumbers && phoneNumbersLength === 1 && isMobile;
	const shouldOpenPhoneDrawer = hasMoreThanOnePhoneNumber && isMobile;
	const isPhoneNumberMenuOpen = Boolean(anchorEl);
	const showPhonesDrawer = Boolean(isMobile && phoneNumbers && phoneNumbers?.length > 1);
	const formattedUnitName = normalizeText(unitName ?? '');

	const handleCloseSnackbar = () => {
		setShowEmptyPhoneSnackbar(false);
	};

	const handleOpenPhoneMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (isPhoneNumberEmpty) {
			setShowEmptyPhoneSnackbar(true);
			return;
		}

		if (shouldCallDirectlyToOnlyPhone) {
			const phone = phoneNumbers[0];
			window.location.href = `tel:${phone}`;
			return;
		}

		if (shouldOpenPhoneDrawer) {
			setOpenPhoneDrawerState(true);
			return;
		}
		setAnchorEl(event.currentTarget);
	};

	const handleClosePhoneMenu = () => {
		setAnchorEl(null);
	};

	const handleTogglePhoneDrawer = () => {
		setOpenPhoneDrawerState(prev => !prev);
	};

	const getStackDirection = () => {
		if (isNotSelf) return 'column';
		return isMobile ? 'row' : 'column';
	};

	const getStackAlign = () => {
		if (isNotSelf) return 'flex-start';
		return isMobile ? 'center' : undefined;
	};

	const addressToDisplay = isTelemedicine ? telehealthAddress : address;

	const handleToggleDetailsDrawer = () => {
		setShowDetailsDrawerState(prev => !prev);
	};

	return {
		dataTagId,
		distanceFilterDescription,
		handleOpenGoogleMaps,
		isPhoneNumberMenuOpen,
		showPhonesDrawer,
		handleCloseSnackbar,
		handleOpenPhoneMenu,
		handleClosePhoneMenu,
		handleTogglePhoneDrawer,
		getStackDirection,
		getStackAlign,
		addressToDisplay,
		handleToggleDetailsDrawer,
		openPhoneDrawerState,
		showEmptyPhoneSnackbar,
		showDetailsDrawerState,
		anchorEl,
		setShowDetailsDrawerState,
		isMobile,
		formattedUnitName,
	};
}
